import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import * as SC from "SC";
import { httpService } from "api/httpService";
import { useAppSelector } from "store/hooks";

const PLAN_LABEL = {
  starter: { label: "Starter", color: "default" },
  professional: { label: "Professional", color: "primary" },
  enterprise: { label: "Enterprise", color: "success" },
};

const STATUS_LABEL = {
  active: { label: "Active", color: "success" },
  trialing: { label: "Trial", color: "info" },
  past_due: { label: "Past Due", color: "warning" },
  canceled: { label: "Canceled", color: "error" },
  incomplete: { label: "Incomplete", color: "warning" },
  paused: { label: "Paused", color: "default" },
};

const METRIC_LABEL = {
  vehicles: "Vehicles",
  drivers: "Drivers & Dispatchers",
  rides_month: "Rides this month",
};

const formatLimit = (limit) =>
  limit >= 2_000_000_000 ? "Unlimited" : String(limit);

const computePercent = (current, limit) => {
  if (!limit || limit >= 2_000_000_000) return null;
  return Math.min(100, Math.round((current / limit) * 100));
};

export const TenantDashboardPage = () => {
  const branding = useAppSelector((state) => state.branding);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { value, error } = await httpService({
        url: "/tenants/usage",
        options: { method: "GET" },
      });
      if (cancelled) return;
      if (error) {
        setError(error);
      } else {
        setUsage(value);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const metrics = useMemo(() => usage?.metrics || [], [usage]);
  const planMeta = PLAN_LABEL[usage?.plan] || PLAN_LABEL.starter;
  const statusMeta =
    STATUS_LABEL[usage?.planStatus] || { label: usage?.planStatus || "—", color: "default" };

  return (
    <SC.OverflowedContainer>
      <Stack gap={3} sx={{ p: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
          <Stack>
            <Typography variant="h5" fontWeight={800}>
              {branding.companyName || "Your Company"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tenant overview
            </Typography>
          </Stack>
          <Stack direction="row" gap={1} alignItems="center">
            <Chip label={planMeta.label} color={planMeta.color} />
            <Chip label={statusMeta.label} color={statusMeta.color} variant="outlined" />
          </Stack>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        {loading ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <Stack direction={{ xs: "column", md: "row" }} gap={2}>
            {metrics.map((m) => {
              const percent = computePercent(m.current, m.limit);
              return (
                <Card key={m.metric} variant="outlined" sx={{ flex: 1, borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      {METRIC_LABEL[m.metric] || m.metric}
                    </Typography>
                    <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5 }}>
                      {m.current}
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                        / {formatLimit(m.limit)}
                      </Typography>
                    </Typography>
                    {percent !== null && (
                      <Box sx={{ mt: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={percent}
                          color={percent > 90 ? "error" : percent > 70 ? "warning" : "primary"}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}

        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1.5 }}>
              Quick actions
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
              <SC.ControlButton
                component={Link}
                to="/booking/schedule_a_ride"
                variant="outlined"
                sx={{ flex: 1 }}
              >
                Schedule a ride
              </SC.ControlButton>
              <SC.ControlButton
                component={Link}
                to="/settings/manage_users"
                variant="outlined"
                sx={{ flex: 1 }}
              >
                Invite a team member
              </SC.ControlButton>
              <SC.ControlButton
                component={Link}
                to="/billing"
                variant="contained"
                sx={{ flex: 1 }}
              >
                Manage billing
              </SC.ControlButton>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </SC.OverflowedContainer>
  );
};

export default TenantDashboardPage;
